import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Mail, MessageCircle, Webhook, Send, PanelBottom } from "lucide-react";

const inputCls = "bg-slate-900 border-slate-700 text-slate-100";

function Section({ icon: Icon, title, subtitle, children, accent = "text-indigo-400" }) {
  return (
    <div className="bg-[#111827] border border-slate-800 rounded-xl p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Icon size={18} className={accent} />
        <div>
          <h2 className="font-heading font-semibold text-slate-100">{title}</h2>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [smtp, setSmtp] = useState({ host: "", port: "587", username: "", password: "", from_name: "", from_email: "" });
  const [pabbly, setPabbly] = useState("");
  const [throttles, setThrottles] = useState({ email_throttle: 2, wa_throttle: 1 });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState("");
  const [footer, setFooter] = useState({ company_name: "", address: "", support_email: "", support_phone: "", website: "", facebook: "", instagram: "", youtube: "" });

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      if (r.data.smtp) setSmtp({ ...r.data.smtp, port: String(r.data.smtp.port || "587") });
      if (r.data.pabbly_webhook_url) setPabbly(r.data.pabbly_webhook_url);
      if (r.data.footer) setFooter((f) => ({ ...f, ...r.data.footer }));
      setThrottles({ email_throttle: r.data.email_throttle || 2, wa_throttle: r.data.wa_throttle || 1 });
    }).catch(() => {});
  }, []);

  const saveSmtp = async () => {
    setBusy("smtp");
    try {
      await api.put("/settings", { smtp: { ...smtp, port: parseInt(smtp.port) || 587 }, email_throttle: parseFloat(throttles.email_throttle) || 2 });
      toast.success("SMTP settings saved");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  };

  const testEmail = async () => {
    if (!testTo) return toast.error("Enter an email to send the test to");
    setBusy("test");
    try {
      await saveSmtp();
      await api.post("/settings/test-email", { to_email: testTo });
      toast.success(`Test email sent to ${testTo}`);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  };

  const savePabbly = async () => {
    setBusy("pabbly");
    try {
      await api.put("/settings", { pabbly_webhook_url: pabbly, wa_throttle: parseFloat(throttles.wa_throttle) || 1 });
      toast.success("WhatsApp settings saved");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  };

  const saveFooter = async () => {
    setBusy("footer");
    try {
      await api.put("/settings", { footer });
      toast.success("Email footer saved — it will appear on every email");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const webhookUrl = settings?.incoming_webhook_url || "";

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-indigo-400 mb-1">Integrations & delivery</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-50">Settings</h1>
      </div>

      <Section icon={Mail} title="SMTP (xSMTP) — email delivery" subtitle="Paste the SMTP credentials from your xSMTP plan.">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-slate-400 text-xs">SMTP Host</Label><Input data-testid="smtp-host-input" value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} placeholder="smtp.xsmtp.co" className={inputCls} /></div>
          <div className="space-y-1"><Label className="text-slate-400 text-xs">Port</Label><Input data-testid="smtp-port-input" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: e.target.value })} placeholder="587" className={inputCls} /></div>
          <div className="space-y-1"><Label className="text-slate-400 text-xs">Username</Label><Input data-testid="smtp-username-input" value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} className={inputCls} /></div>
          <div className="space-y-1"><Label className="text-slate-400 text-xs">Password</Label><Input data-testid="smtp-password-input" type="password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} className={inputCls} /></div>
          <div className="space-y-1"><Label className="text-slate-400 text-xs">From name</Label><Input data-testid="smtp-from-name-input" value={smtp.from_name} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} placeholder="SND Store" className={inputCls} /></div>
          <div className="space-y-1"><Label className="text-slate-400 text-xs">From email</Label><Input data-testid="smtp-from-email-input" value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} placeholder="offers@yourdomain.com" className={inputCls} /></div>
          <div className="space-y-1"><Label className="text-slate-400 text-xs">Emails per second</Label><Input data-testid="smtp-throttle-input" type="number" step="0.5" min="0.5" value={throttles.email_throttle} onChange={(e) => setThrottles({ ...throttles, email_throttle: e.target.value })} className={inputCls} /></div>
        </div>
        {smtp.host.toLowerCase().includes("gmail") && (
          <p data-testid="gmail-warning" className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
            Gmail SMTP rewrites the From address to the Gmail account — your emails will NOT come from your own domain. Use your xSMTP credentials (connect.xsmtp.co) to send as @staranddaisy.com.
          </p>
        )}
        <p className="text-[11px] text-slate-500">Every campaign email is sent individually — each customer only ever sees their own address, never other recipients.</p>
        <div className="flex flex-wrap gap-3 items-end">
          <Button data-testid="save-smtp-button" onClick={saveSmtp} disabled={busy === "smtp"} className="bg-indigo-500 hover:bg-indigo-600 text-white">{busy === "smtp" ? "Saving…" : "Save SMTP"}</Button>
          <div className="flex gap-2 items-center flex-1 min-w-[240px]">
            <Input data-testid="test-email-input" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className={inputCls} />
            <Button data-testid="test-smtp-button" variant="outline" onClick={testEmail} disabled={busy === "test"} className="border-slate-700 text-slate-200 hover:bg-slate-800 shrink-0">
              <Send size={14} className="mr-1.5" /> {busy === "test" ? "Sending…" : "Send test"}
            </Button>
          </div>
        </div>
      </Section>

      <Section icon={MessageCircle} title="WhatsApp via Pabbly" subtitle="Paste your Pabbly Connect webhook URL. The message template lives in Pabbly; this app sends the variables." accent="text-emerald-400">
        <div className="grid sm:grid-cols-[1fr_140px] gap-3">
          <div className="space-y-1"><Label className="text-slate-400 text-xs">Pabbly webhook URL</Label><Input data-testid="pabbly-url-input" value={pabbly} onChange={(e) => setPabbly(e.target.value)} placeholder="https://connect.pabbly.com/workflow/sendwebhookdata/…" className={inputCls} /></div>
          <div className="space-y-1"><Label className="text-slate-400 text-xs">Messages per second</Label><Input data-testid="wa-throttle-input" type="number" step="0.5" min="0.5" value={throttles.wa_throttle} onChange={(e) => setThrottles({ ...throttles, wa_throttle: e.target.value })} className={inputCls} /></div>
        </div>
        <Button data-testid="save-pabbly-button" onClick={savePabbly} disabled={busy === "pabbly"} className="bg-emerald-500 hover:bg-emerald-600 text-white">{busy === "pabbly" ? "Saving…" : "Save WhatsApp settings"}</Button>
      </Section>

      <Section icon={PanelBottom} title="Email footer branding" subtitle="Shown at the bottom of every email, above the unsubscribe link." accent="text-rose-400">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-slate-400 text-xs">Company name</Label><Input data-testid="footer-company-input" value={footer.company_name} onChange={(e) => setFooter({ ...footer, company_name: e.target.value })} placeholder="StarAndDaisy" className={inputCls} /></div>
          <div className="space-y-1"><Label className="text-slate-400 text-xs">Address</Label><Input data-testid="footer-address-input" value={footer.address} onChange={(e) => setFooter({ ...footer, address: e.target.value })} placeholder="Registered office address" className={inputCls} /></div>
          <div className="space-y-1"><Label className="text-slate-400 text-xs">Support email</Label><Input data-testid="footer-support-email-input" value={footer.support_email} onChange={(e) => setFooter({ ...footer, support_email: e.target.value })} placeholder="support@staranddaisy.com" className={inputCls} /></div>
          <div className="space-y-1"><Label className="text-slate-400 text-xs">Support phone</Label><Input data-testid="footer-support-phone-input" value={footer.support_phone} onChange={(e) => setFooter({ ...footer, support_phone: e.target.value })} placeholder="+91 …" className={inputCls} /></div>
          <div className="space-y-1"><Label className="text-slate-400 text-xs">Website URL</Label><Input data-testid="footer-website-input" value={footer.website} onChange={(e) => setFooter({ ...footer, website: e.target.value })} placeholder="https://staranddaisy.com" className={inputCls} /></div>
          <div className="space-y-1"><Label className="text-slate-400 text-xs">Facebook URL</Label><Input data-testid="footer-facebook-input" value={footer.facebook} onChange={(e) => setFooter({ ...footer, facebook: e.target.value })} className={inputCls} /></div>
          <div className="space-y-1"><Label className="text-slate-400 text-xs">Instagram URL</Label><Input data-testid="footer-instagram-input" value={footer.instagram} onChange={(e) => setFooter({ ...footer, instagram: e.target.value })} className={inputCls} /></div>
          <div className="space-y-1"><Label className="text-slate-400 text-xs">YouTube URL</Label><Input data-testid="footer-youtube-input" value={footer.youtube} onChange={(e) => setFooter({ ...footer, youtube: e.target.value })} className={inputCls} /></div>
        </div>
        <Button data-testid="save-footer-button" onClick={saveFooter} disabled={busy === "footer"} className="bg-rose-700 hover:bg-rose-800 text-white">{busy === "footer" ? "Saving…" : "Save footer"}</Button>
      </Section>

      <Section icon={Webhook} title="Incoming orders webhook" subtitle="POST new orders from your website here — they appear in your customer database automatically." accent="text-amber-400">
        <div className="flex gap-2 items-center">
          <code data-testid="incoming-webhook-url" className="flex-1 text-xs font-mono bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-300 truncate">{webhookUrl || "…"}</code>
          <Button data-testid="copy-webhook-url-button" variant="outline" size="sm" onClick={() => copy(webhookUrl)} className="border-slate-700 text-slate-300 hover:bg-slate-800"><Copy size={14} /></Button>
        </div>
        <pre className="text-[11px] font-mono bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-400 overflow-x-auto">{`curl -X POST "${webhookUrl}" \\
  -H "Content-Type: application/json" \\
  -d '{"date":"2025-06-01","name":"Ravi Kumar","phone":"+919876543210",
       "email":"ravi@example.com","product_name":"Walker Pro",
       "pin_code":"400001","city":"Mumbai"}'`}</pre>
      </Section>
    </div>
  );
}
